import {
  Slider,
  SliderTrack,
  SliderThumb,
  type SliderProps,
} from "react-aria-components";
import { CoreSlider, PropsWithOptionalTestID } from "@boi/components-core";
import { SliderHandleIcon } from "@boi/react-icons";

import * as S from "./SliderBase.styled";
import {
  useNotchTooltip,
  useSafeSliderNotches,
  useNotchesToRelativePercent,
  useSliderValueAsPercent,
  useCalculateArrowVisualStatus,
} from "./hooks";

export type SliderTooltipNotch = {
  startValue: number;
  tooltip: string;
};
export type SliderNotches = undefined | number[] | SliderTooltipNotch[];

export type SliderBaseProps = PropsWithOptionalTestID &
  Omit<SliderProps, "value"> & {
    value: number;
    notches?: SliderNotches;
  };

function SliderBase(props: SliderBaseProps) {
  return (
    <S.MainContainer>
      <Slider {...props} />
    </S.MainContainer>
  );
}

type NotchesProps = PropsWithOptionalTestID &
  Pick<SliderBaseProps, "notches" | "minValue" | "maxValue">;

function Notches(props: NotchesProps) {
  const notchesAsPercent = useNotchesToRelativePercent(props);
  const { testID = "slider" } = props;

  return (
    <S.NotchBlockContainer data-testid={`${testID}-notches`}>
      {notchesAsPercent?.map((notchAsPercent: number, index: number) => (
        <S.NotchBlock key={index} $notchAsPercent={notchAsPercent} />
      ))}
    </S.NotchBlockContainer>
  );
}

SliderBase.UI = (props: SliderBaseProps) => {
  const {
    notches,
    minValue = CoreSlider.DEFAULT_MIN_VALUE,
    maxValue = CoreSlider.DEFAULT_MAX_VALUE,
    testID = "slider",
  } = props;

  const safeNotches = useSafeSliderNotches(minValue, maxValue, notches);
  const sliderPercent = useSliderValueAsPercent(props);
  const notchTooltipText = useNotchTooltip(props);
  const visualStatus = useCalculateArrowVisualStatus(notches, notchTooltipText);

  return (
    <S.UIContainer>
      <SliderTrack>
        {safeNotches && <Notches {...props} />}
        <SliderThumb>
          <SliderHandleIcon />
        </SliderThumb>
      </SliderTrack>
      <S.TooltipArrow
        $visualState={visualStatus}
        $leftPercent={sliderPercent}
        data-testid={`${testID}-tooltip-arrow`}
      />
    </S.UIContainer>
  );
};

export default SliderBase;
